import { ref, computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'

const CREATE_PROJECT_LABEL = gql`
  mutation CreateLabel($projectId: ID!, $title: String!, $color: String!) {
    createLabel(projectId: $projectId, title: $title, color: $color) {
      label {
        id
        title
        color
      }
      errors
    }
  }
`

const CREATE_PERSONAL_LABEL = gql`
  mutation CreatePersonalLabel($title: String!, $color: String!) {
    createPersonalLabel(title: $title, color: $color) {
      personalLabel {
        id
        title
        color
      }
      errors
    }
  }
`

export function useCreateLabel(scope, projectId) {
  const creating = ref(false)
  const error = ref('')

  const createMutation = computed(() => {
    return scope === 'project' ? CREATE_PROJECT_LABEL : CREATE_PERSONAL_LABEL
  })

  const { mutate: createLabelMutation } = useMutation(createMutation, {
    errorPolicy: 'all', // Return partial data even when there are GraphQL errors
  })

  async function createLabel(title, color) {
    creating.value = true
    error.value = ''

    try {
      const mutationVars =
        scope === 'project'
          ? {
              projectId: projectId,
              title: title,
              color: color,
            }
          : {
              title: title,
              color: color,
            }

      let result
      try {
        result = await createLabelMutation(mutationVars)
      } catch (e) {
        // GraphQL might throw when trying to resolve non-nullable fields on null
        // Try to extract validation errors from the error response
        if (e.graphQLErrors && e.graphQLErrors.length > 0) {
          const graphqlError = e.graphQLErrors[0]
          // Check if the error is about null for non-nullable field
          if (graphqlError.message && graphqlError.message.includes('null for non-nullable')) {
            // This usually means validation failed - try to get errors from extensions or path
            // For now, provide a user-friendly message
            error.value =
              scope === 'project'
                ? 'A label with this title already exists'
                : 'A personal label with this title already exists'
          } else {
            error.value = graphqlError.message || 'Failed to create label'
          }
        } else {
          error.value = e.message || 'Failed to create label'
        }
        return { success: false, label: null, error: error.value }
      }

      const { data, errors: graphqlErrors } = result || {}
      const resultField = scope === 'project' ? 'createLabel' : 'createPersonalLabel'
      const labelField = scope === 'project' ? 'label' : 'personalLabel'

      // Check for GraphQL errors (e.g., null for non-nullable field)
      if (graphqlErrors && graphqlErrors.length > 0) {
        // Try to extract validation errors from the mutation result first
        if (data?.[resultField]?.errors?.length) {
          error.value = data[resultField].errors.join(', ')
        } else {
          // Fall back to GraphQL error message, but make it user-friendly
          const errorMessage = graphqlErrors[0]?.message || 'Failed to create label'
          if (errorMessage.includes('null for non-nullable')) {
            // This usually means validation failed (duplicate title, etc.)
            error.value =
              scope === 'project'
                ? 'A label with this title already exists'
                : 'A personal label with this title already exists'
          } else {
            error.value = errorMessage
          }
        }
        return { success: false, label: null, error: error.value }
      }

      // Check for mutation-level errors (validation errors from the service)
      if (data?.[resultField]?.errors?.length) {
        error.value = data[resultField].errors.join(', ')
        return { success: false, label: null, error: error.value }
      }

      const label = data?.[resultField]?.[labelField]
      if (!label) {
        error.value =
          scope === 'project' ? 'Failed to create label' : 'Failed to create personal label'
        return { success: false, label: null, error: error.value }
      }

      return { success: true, label: label, error: null }
    } catch (e) {
      // Handle network errors or other exceptions
      error.value = e.message || 'Failed to create label'
      return { success: false, label: null, error: error.value }
    } finally {
      creating.value = false
    }
  }

  return {
    creating,
    error,
    createLabel,
  }
}
